import * as ENGINE from '@gnsx/genesys.js';
import { invoke } from 'es-toolkit/compat';

import { getPrefabAssetName, isPrefabAsset } from '../objectHelpers';

import { reloadActor } from './sceneActions';

import type { UniversalAccessor } from '../universalAccessor';
import type { EditorObjectData } from '@/app/editor/core/editorClassData';
import type { IObjectTreeNode } from '@/editor/store/models/objectTree';


import { requestViewportRender, ViewportRenderReason } from '@/editor/components/viewport/canvas/viewportRenderScheduler';
import { nameHelpers } from '@/editor/core';
import { reportEditorHookError } from '@/editor/core/editorHookErrorReporter';
import { actorLogRef, notifyPropertyTargetRef, universalAccessorForLog } from '@/editor/core/engineLogRefs';
import { attachToGroup, detachGroupChildren } from '@/editor/core/objectHelpers';
import { editorAssets, editorStore, sceneStore } from '@/editor/store';
import { ObjectTreeNode } from '@/editor/store/models/objectTree';
import { castAs } from '@/lib/utils';

const DEBUG_LOG = {
  notifyEditorPropertyChanged: false,
} as const;

/**
 * Object actions - object/actor/asset actions
 */

/**
 * Load an actor instance from a JSON object/string. Supports prefab instances.
 */
export async function loadActorAsync(actorData: ENGINE.SerializedData | string) {
  try {
    const parsedData: ENGINE.SerializedData = typeof actorData === 'string'
      ? ENGINE.parseJson(actorData) as ENGINE.SerializedData
      : actorData;
    const actor = await ENGINE.WorldSerializer.loadActor(
      parsedData,
      editorAssets.prefabDataCopy,
      true
    );
    return actor instanceof ENGINE.Actor ? actor : undefined;
  } catch (error) {
    return undefined;
  }
}

/**
 * Load an actor instance from a JSON object/string. Supports prefab instances.
 * Sync compatibility wrapper for existing callsites.
 */
export function loadActor(actorData: ENGINE.SerializedData | string) {
  try {
    const parsedData: ENGINE.SerializedData = typeof actorData === 'string'
      ? ENGINE.parseJson(actorData) as ENGINE.SerializedData
      : actorData;
    const actor = ENGINE.WorldSerializer.loadActorSync(
      parsedData,
      editorAssets.prefabDataCopy,
      true
    );
    return actor instanceof ENGINE.Actor ? actor : undefined;
  } catch (error) {
    return undefined;
  }
}


/** Options for batch-friendly actor duplication (see `duplicateActor`). */
export type DuplicateActorOptions = {
  uniqueName?: boolean;
  /** When set, skips `world.getActors()` name scan; caller must push each chosen name after use. */
  existingNames?: string[];
  /** When true, exports via `WorldSerializer.exportObject` only — caller must detach selection group first if needed. */
  skipSelectionGroupHandling?: boolean;
};

function resolveDuplicateActorOptions(
  uniqueNameOrOptions: boolean | DuplicateActorOptions,
): DuplicateActorOptions & { uniqueName: boolean } {
  if (typeof uniqueNameOrOptions === 'boolean') {
    return { uniqueName: uniqueNameOrOptions };
  }
  return {
    ...uniqueNameOrOptions,
    uniqueName: uniqueNameOrOptions.uniqueName ?? true,
  };
}

/**
 * Load a new actor instance by duplicating an existing actor.
 *
 * @param uniqueNameOrOptions - Pass `boolean` for backward compatibility (`uniqueName`), or an options object for batch duplication.
 */
export function duplicateActor(
  actor: ENGINE.Actor,
  uniqueNameOrOptions: boolean | DuplicateActorOptions = true,
): ENGINE.Actor | null {
  try {
    const opts = resolveDuplicateActorOptions(uniqueNameOrOptions);
    const selectionGroup = sceneStore.selectionGroup;
    let wasInGroup = false;

    if (!opts.skipSelectionGroupHandling && sceneStore.scene && selectionGroup) {
      if (actor.rootComponent.parent === selectionGroup) {
        wasInGroup = true;
        detachGroupChildren(selectionGroup, sceneStore.scene);
      }
    }

    const duplicatedActor = ENGINE.WorldSerializer.duplicateActor(actor, editorAssets.prefabDataCopy, true);

    if (wasInGroup && selectionGroup) {
      attachToGroup(selectionGroup, [actor]);
    }

    if (duplicatedActor && opts.uniqueName) {
      const currentName = nameHelpers.getActorDisplayName(duplicatedActor);
      let uniqueActorName: string;

      if (opts.existingNames) {
        uniqueActorName = nameHelpers.makeUniqueName(currentName, opts.existingNames, { generatedSeparator: ' ' });
        opts.existingNames.push(uniqueActorName);
      } else {
        const root = sceneStore.outliner.rootNode ?? undefined;
        const existingNames = nameHelpers.collectOutlinerNames(root);
        uniqueActorName = nameHelpers.makeUniqueName(currentName, existingNames, { generatedSeparator: ' ' });
      }

      nameHelpers.setCustomDisplayName(duplicatedActor, uniqueActorName);
    }

    return duplicatedActor ?? null;
  } catch (error) {
    console.error('Error duplicating actor:', actorLogRef(actor), error);
    return null;
  }
}

/**
 * Load a new component instance by duplicating an existing component.
 * @param componentAccessor - Universal accessor pointing to the component to duplicate
 * @param selectNewNode - Whether to select the new node
 * @param preCalculatedUuid - Pre-calculated UUID for the duplicated component
 * @returns The duplicated component and created tree node
 */
export function duplicateComponentInternal(
  componentAccessor: UniversalAccessor,
  selectNewNode: boolean = true,
  preCalculatedUuid?: string
): { duplicatedComponent: ENGINE.SceneComponent | null; createdNode: IObjectTreeNode | undefined } {
  try {
    const component = componentAccessor.resolveComponent();
    if (!component) {
      console.warn('duplicateComponentInternal: Failed to resolve component from accessor');
      return { duplicatedComponent: null, createdNode: undefined };
    }

    // Get the parent actor to find the correct EditableObject and its component tree
    const actor = componentAccessor.resolveActor();
    if (!actor) {
      console.warn('duplicateComponentInternal: Failed to resolve actor from accessor');
      return { duplicatedComponent: null, createdNode: undefined };
    }

    // Find the EditableObject for this actor
    const editableObject = sceneStore.selectedObjects.find(obj => obj.asActor?.uuid === actor.uuid);
    if (!editableObject) {
      console.warn('duplicateComponentInternal: Actor is not in selectedObjects, cannot access component tree');
      return { duplicatedComponent: null, createdNode: undefined };
    }

    // Get the component tree (ObjectTree) from the EditableObject
    const tree = editableObject.hierarchy;

    // Clone through engine helper to keep serialization logic centralized.
    const duplicatedComponent = ENGINE.WorldSerializer.duplicateComponent(component, true);

    if (!(duplicatedComponent instanceof ENGINE.SceneComponent)) {
      return { duplicatedComponent: null, createdNode: undefined };
    }

    // Assign the pre-calculated UUID if provided
    if (preCalculatedUuid) {
      duplicatedComponent.uuid = preCalculatedUuid;
    }

    // Generate unique name for the duplicated component
    const existingComponents = actor.getComponents(ENGINE.SceneComponent);
    const existingNames = existingComponents.map(c => nameHelpers.getComponentDisplayName(c));
    const currentName = nameHelpers.getComponentDisplayName(duplicatedComponent);
    const uniqueName = nameHelpers.makeUniqueName(currentName, existingNames, { generatedSeparator: ' ' });
    nameHelpers.setCustomDisplayName(duplicatedComponent, uniqueName);

    let createdNode: IObjectTreeNode | undefined;

    // Determine where to attach the duplicate - same parent as original component
    const attachTo = component.isRootComponent() ? component : component.parent;
    if (attachTo) {
      attachTo.add(duplicatedComponent);
      try {
        duplicatedComponent.onEditorAddToWorld();
      } catch (error) {
        reportEditorHookError({
          hook: 'onEditorAddToWorld',
          context: 'objectActions.duplicateComponentInternal',
          target: duplicatedComponent,
          error,
        });
        return { duplicatedComponent: null, createdNode: undefined };
      }

      // Create the tree node for the duplicated component
      const attachToNode = tree.getNodeById(attachTo.uuid);
      if (attachToNode) {
        const componentNode = ObjectTreeNode.create({
          id: duplicatedComponent.uuid,
          name: nameHelpers.getComponentDisplayName(duplicatedComponent)
        });
        componentNode.init(duplicatedComponent);
        tree.addNode(componentNode, attachToNode);
        createdNode = componentNode;

        // Notify changed - similar to addComponentInternal
        const editableObjectNode = attachToNode.editableObjectParent;
        editableObjectNode?.notifyPropertyChanged('Duplicate component', actor.name + '.' + duplicatedComponent.name, () => actor);

        // Select new component node
        if (selectNewNode) {
          tree.selectNode(duplicatedComponent.uuid);

          // Scroll to the duplicated component
          requestAnimationFrame(() => {
            tree.scrollToSelected();
          });
        }
      }
    }

    // Update inspector
    sceneStore.updateInspector();

    return { duplicatedComponent, createdNode };
  } catch (error) {
    console.error('Error duplicating component:', universalAccessorForLog(componentAccessor), error);
    reportEditorHookError({
      hook: 'onEditorAddToWorld',
      context: 'objectActions.duplicateComponentInternal',
      target: componentAccessor.resolveComponent() ?? undefined,
      error,
    });
    return { duplicatedComponent: null, createdNode: undefined };
  }
}

/**
 * Export a single actor with group reattach logic to handle selection group children.
 */
export function exportActor(actor: ENGINE.Actor): ENGINE.SerializedData | null {
  try {
    // Check if actor is in a selection group and handle detachment/reattachment
    let wasInGroup = false;
    const selectionGroup = sceneStore.selectionGroup;

    if (sceneStore.scene && selectionGroup) {
      const rootComponent = actor.rootComponent;
      if (rootComponent.parent === selectionGroup) {
        wasInGroup = true;
        // Detach from group temporarily using helper function
        detachGroupChildren(selectionGroup, sceneStore.scene);
      }
    }

    // Export the actor
    const actorData = ENGINE.WorldSerializer.exportObject(actor);

    // Reattach to group if it was originally in one
    if (wasInGroup && selectionGroup) {
      attachToGroup(selectionGroup, [actor]);
    }

    return actorData;
  } catch (error) {
    console.error('Error exporting actor:', actorLogRef(actor), error);
    return null;
  }
}

/**
 * Notify object that a property has changed in editor.
 */
export function notifyEditorPropertyChanged(target: any, path: string, value: any): ENGINE.EditorPropertyChangedResult {
  const result: ENGINE.EditorPropertyChangedResult = {};

  if (path.startsWith('options.')) {
    result.isOptions = true;
    path = path.replace('options.', '');
  }
  try {
    invoke(target, 'onEditorPropertyChanged', [path, value, result]);
  } catch (error) {
    reportEditorHookError({
      hook: 'onEditorPropertyChanged',
      context: `objectActions.notifyEditorPropertyChanged (${path})`,
      target,
      error,
    });
  }

  if (DEBUG_LOG.notifyEditorPropertyChanged) {
    console.log('notifyEditorPropertyChanged', path, value, result, notifyPropertyTargetRef(target));
  }
  requestViewportRender(ViewportRenderReason.EditorStateChanged);
  return result;
}

/**
 * Remove a component from an object.
 * @param accessor - Universal accessor pointing to the component to remove
 * @returns
 */
export function removeComponentInternal(accessor: UniversalAccessor): void {
  const component = accessor.resolveComponent();
  if (!component) return;

  // Get the parent actor to find the correct EditableObject and its component tree
  const actor = accessor.resolveActor();
  if (!actor) {
    console.warn('removeComponentInternal: Failed to resolve actor from accessor');
    return;
  }

  // Find the EditableObject for this actor
  const editableObject = sceneStore.selectedObjects.find(obj => obj.asActor?.uuid === actor.uuid);
  if (!editableObject) {
    console.warn('removeComponentInternal: Actor is not in selectedObjects, cannot access component tree');
    return;
  }

  // Get the component tree (ObjectTree) from the EditableObject
  const tree = editableObject.hierarchy;

  const fallbackSelectionId = getComponentDeleteFallbackSelectionId(component, actor, tree);

  // Notify the component it is being removed before detaching from the hierarchy
  try {
    component.onEditorRemoveFromWorld();
  } catch (error) {
    reportEditorHookError({
      hook: 'onEditorRemoveFromWorld',
      context: 'objectActions.removeComponentInternal',
      target: component,
      error,
    });
  }

  // Remove the component from the tree
  tree.removeNode(component.uuid);

  // Remove the component from the engine
  component.removeFromParent();

  if (fallbackSelectionId && tree.getNodeById(fallbackSelectionId)) {
    tree.selectNode(fallbackSelectionId);
  }

  // Notify changed
  const editableObjectNode = tree.getNodeById(actor.rootComponent.uuid)?.editableObjectParent;
  editableObjectNode?.notifyPropertyChanged('Remove component', actor.name + '.' + component.name, () => actor);
}

function getComponentDeleteFallbackSelectionId(
  component: ENGINE.SceneComponent,
  actor: ENGINE.Actor,
  tree: { getNodeById: (id: string) => IObjectTreeNode | undefined },
): string | null {
  const parentComponent = component.parent;
  if (parentComponent) {
    const siblingComponents = parentComponent.children.filter(
      child => child instanceof ENGINE.SceneComponent,
    );
    const currentIndex = siblingComponents.findIndex(sibling => sibling.uuid === component.uuid);
    for (let i = currentIndex - 1; i >= 0; i -= 1) {
      const previousSibling = siblingComponents[i];
      if (previousSibling && tree.getNodeById(previousSibling.uuid)) {
        return previousSibling.uuid;
      }
    }

    if (tree.getNodeById(parentComponent.uuid)) {
      return parentComponent.uuid;
    }
  }

  return tree.getNodeById(actor.rootComponent.uuid) ? actor.rootComponent.uuid : null;
}

/**
 * Returns a stringified snapshot of the object and its children.
 */
export function takeActorSnapshot(accessor: UniversalAccessor) {
  const actor = accessor.resolveActor();
  if (!actor) {
    console.warn('[objectActions.takeActorSnapshot] Failed to resolve actor for snapshot');
    return null;
  }
  return ENGINE.WorldSerializer.exportObject(actor);
}

export function applyActorSnapshot(accessor: UniversalAccessor, snapshot: ENGINE.SerializedData | null) {
  if (!snapshot) return;
  const actor = accessor.resolveActor();
  if (!actor) return;

  if (editorStore.isPrefabMode && isPrefabAsset(actor)) {
    void reloadPrefabEditorActor(actor, snapshot);
    return;
  }

  void reloadActor(actor, snapshot);
}

/**
 * Reloads an unattached prefab template actor from a serialized snapshot (prefab editor mode).
 */
export async function reloadPrefabEditorActor(
  current: ENGINE.Actor,
  snapshot: ENGINE.SerializedData,
): Promise<ENGINE.Actor | null> {
  const uuid = current.uuid;
  const prefabAssetPath = getPrefabAssetName(current) ?? sceneStore.prefabEditPath ?? undefined;

  let loaded = await loadActorAsync(snapshot);
  if (!loaded) {
    console.error('[objectActions.reloadPrefabEditorActor] Failed to load actor from snapshot', actorLogRef(current));
    return null;
  }

  if (loaded.uuid !== uuid) {
    const remappedData = ENGINE.WorldSerializer.remapSerializedUuids(
      loaded.asExportedObject() as ENGINE.SerializedData,
      { [loaded.uuid]: uuid },
    );
    const remapped = await loadActorAsync(remappedData);
    if (!remapped) {
      console.error('[objectActions.reloadPrefabEditorActor] Failed to load remapped actor', actorLogRef(current));
      return null;
    }
    loaded = remapped;
  }

  if (prefabAssetPath) {
    (loaded.editorData as EditorObjectData).prefabAsset = prefabAssetPath;
  }

  const editable = sceneStore.selectedObjects.find(obj => obj.id === uuid);
  if (!editable) {
    console.warn('[objectActions.reloadPrefabEditorActor] No editable object for actor', actorLogRef(current));
    return loaded;
  }

  const selectedNodeIds = [...editable.hierarchy.selectedNodeIds];
  const expandedNodeIds = [...editable.hierarchy.expandedNodeIds];
  editable.init(loaded, true);
  editable.hierarchy.setExpandedNodesIds(expandedNodeIds);
  const validSelectedIds = selectedNodeIds.filter(id => editable.hierarchy.getNodeById(id));
  if (validSelectedIds.length > 0) {
    editable.hierarchy.setSelectedNodeIds(validSelectedIds);
  } else {
    editable.hierarchy.selectNode(uuid, false);
  }
  editable.bumpUpdateCounter();

  sceneStore.updateInspector();
  return loaded;
}

/**
 * Add a component to the object hierarchy tree internal implementation.
 * @param parentAccessor - Universal accessor pointing to the parent (Actor or SceneComponent)
 * @param className - The class name of the component to add
 * @param selectNewNode - Whether to select the new node
 * @returns The added component or undefined if failed
 */
export function addComponentInternal(
  parentAccessor: UniversalAccessor,
  className: string,
  selectNewNode: boolean = true,
  preCalculatedUuid?: string
): ENGINE.SceneComponent | undefined {
  const component = castAs(editorAssets.componentClassMap.get(className)?.createInstance(), ENGINE.SceneComponent);

  if (!component) {
    console.warn(`addComponentInternal: Failed to create component: ${className}`);
    return;
  }

  // Assign the pre-calculated UUID if provided
  if (preCalculatedUuid) {
    component.uuid = preCalculatedUuid;
  }

  // Get the parent actor to find the correct EditableObject and its component tree
  const parentActor = parentAccessor.resolveActor();
  if (!parentActor) {
    console.warn('addComponentInternal: Failed to resolve parent actor');
    return;
  }

  // Find the EditableObject for this actor
  const editableObject = sceneStore.selectedObjects.find(obj => obj.asActor?.uuid === parentActor.uuid);
  if (!editableObject) {
    console.warn('addComponentInternal: Actor is not in selectedObjects, cannot access component tree');
    return;
  }

  // Get the component tree (ObjectTree) from the EditableObject
  const tree = editableObject.hierarchy;

  // Get the parent component if applicable
  const parentComponent = parentAccessor.resolveComponent();
  let attachToId = parentAccessor.actorId;

  if (parentComponent) {
    parentComponent.add(component);
    attachToId = parentComponent.uuid;
  } else {
    const rootComponent = parentActor.rootComponent;
    attachToId = rootComponent.uuid;
    rootComponent.add(component);
  }

  try {
    component.onEditorAddToWorld();
  } catch (error) {
    reportEditorHookError({
      hook: 'onEditorAddToWorld',
      context: 'objectActions.addComponentInternal',
      target: component,
      error,
    });
    return;
  }

  const parentNode = tree.getNodeById(attachToId);
  if (!parentNode) return;

  const existingComponentNames = parentActor
    .getComponents(ENGINE.SceneComponent)
    .filter(c => c.uuid !== component.uuid)
    .map(c => c.name)
    .filter((n): n is string => !!n);
  const base = nameHelpers.suggestComponentBaseName(component);
  const uniqueComponentName = nameHelpers.makeUniqueName(base, existingComponentNames, { generatedSeparator: ' ' });
  nameHelpers.setCustomDisplayName(component, uniqueComponentName);

  const componentId = component.uuid;
  const componentNode = ObjectTreeNode.create({
    id: componentId,
    name: uniqueComponentName
  });
  componentNode.init(component);
  tree.addNode(componentNode, parentNode);

  // Notify changed
  const editableObjectNode = parentNode.editableObjectParent;
  editableObjectNode?.notifyPropertyChanged('Add component', parentActor.name + '.' + component.name, () => parentActor);

  // Select new component node
  if (selectNewNode) {
    // Ensure parent is expanded so the new node is visible
    if (!parentNode.isExpanded) {
      parentNode.setExpanded(true);
    }
    tree.selectNode(componentId);

    // Scroll to the newly added component
    tree.scrollToSelected();
  }

  return component;
}